import React from "react";
import {
  Box,
  Button,
  ButtonRow,
  Card,
  ErrorIcon,
  Heading,
  PageState,
  Text,
  WarningIcon
} from "@myob/myob-widgets";
import "./Common.css"
import notFound from "../assets/no-results-found.svg"
import somethingWrong from "../assets/something-went-wrong.svg"
import * as PropTypes from "prop-types";
import {redirectTo} from "./utils";

function CustomTitle(text, type = "") {
  return (
    <Box
      display="flex"
      alignItems={type === "" ? "flex-start" : "center"}
      justifyContent={type === "" ? "flex-start" : "center"}
      textAlign="left"
    >
      {type === "" ? (
        <Heading level="1">{text}</Heading>
      ) : (
        <>
          {type === "warning" ? (
            <Box marginRight="3xs">
              <WarningIcon color="warning" />
            </Box>
          ) : (
            <Box justifyContent="center" marginRight="3xs">
              <ErrorIcon color="danger" />
            </Box>
          )}
          {text}
        </>
      )}
    </Box>
  );
}

function ErrorView({type, redirectUrl}) {

  const handleCancel = () => {
    redirectTo(redirectUrl);
  }

  const errorTypes = {
    notFound: {
      title: "Page Not Found",
      description: 'This page doesn\'t exist',
      img: notFound,
      alt: "page not found"
    },
    somethingWentWrong: {
      title: "Something went wrong",
      description: "If the issue persists, contact MYOB support",
      img: somethingWrong,
      alt: "something went wrong",
    },
    applicationCannotProceed: {
      title: "Application cannot proceed",
      description: "Application cannot be reverted to previous status. Please start a new application if you wish to connect a new bank feed.",
      img: somethingWrong,
      alt: "application cannot proceed",
    },
    applicationFailed: {
      title: "Application failed",
      description: "If the issue persists, contact MYOB support",
      img: somethingWrong,
      alt: "application failed",
    },
    applicationResultFailed: {
      title: "Page Not Found",
      description: 'This page doesn\'t exist',
      img: notFound,
      alt: "page not found",
    },
    finalisationFailed: {
      title: "Finalisation failed",
      description: "Accounts cannot be connected, please try again. If the issue persists, contact MYOB support",
      img: somethingWrong,
      alt: "finalisation failed",
    },
    accountsNotConnected: {
      title: CustomTitle("Accounts not connected", "error"),
      description: (
        <Box style={{paddingLeft: 8}}>
          <Text textAlign="center" style={{marginBottom: 16}}> Your accounts were not connected because you stopped the setup.
            We have not saved any account data</Text>
          {!redirectUrl && <Text textAlign="center">If you still want to connect your bank or credit cards, close this window and start the setup again.</Text>}
        </Box>)
    },
    userOptOutWhenEdit: {
      title: CustomTitle("User opted out", "error"),
      description: (
        <Box style={{paddingLeft: 8}}>
          <Text textAlign="center" style={{marginBottom: 16}}> Placeholder：need to change the content when cancel consent during reconsent.</Text>
        </Box>
      ),
    },
    openBankingUsrDhNotListedError: {
      title: CustomTitle("Unable to connect to this bank", "warning"),
      description: (
        <Box style={{paddingLeft: 8}}>
          <Text textAlign="center" style={{marginBottom: 16}}>This bank does not support connections to MYOB.</Text>
          {!redirectUrl && <Text textAlign="center">To connect a different bank, close this window and start the setup again.</Text>}
        </Box>
      ),
    },
    openBankingGenericRedirectError: {
      title: CustomTitle("Something went wrong", "error"),
      description: (
        !redirectUrl && <Box style={{paddingLeft: 8}}>
          <Text textAlign="center">To try again, close this window and start the setup again.</Text>
        </Box>
      ),
    },
    unableToRenewConnection: {
      title: CustomTitle("Unable to renew connection"),
      description: (
        <Box textAlign="left" >
          <Text textAlign="left" >
            To renew your connection for another year, you need to disconnect and then reconnect your account.
          </Text>
          <Heading level="2" textAlign="left">
            How to disconnect and reconnect your account
          </Heading>
          <Box marginLeft="3xs" marginTop="3xs">
          <Text textAlign="left" marginBottom="3xs">
            <b>1.</b> Go to <b>More</b> and tap <b>Connected accounts</b>.
          </Text>
          <Text textAlign="left" marginBottom="3xs">
            <b>2.</b> Tap the three dots (...) on the account and tap <b>Disconnect</b>.
          </Text>
          <Text textAlign="left" marginBottom="3xs">
            <b>3.</b> Tap <b>Disconnect</b> to confirm.
          </Text>
          <Text textAlign="left" marginBottom="3xs">
            <b>4.</b> Tap <b>Connect to your bank</b> and follow the set-up prompts.
          </Text>
          </Box>
        </Box>
      ),
    },
    openBankingFinaliseError: {
      title: "Looks like something went wrong on our end",
      description: "To try again, close this window and start the setup again.",
      img: somethingWrong,
      alt: "open banking finalise error",
    },
    noAccountsError: {
      title: CustomTitle("Accounts not connected", "error"),
      description: (
        <Box style={{paddingLeft: 8}}>
          <Text textAlign="center" style={{marginBottom: 16}}> No accounts were selected in the setup.
            We have not saved any account data.</Text>
          {!redirectUrl && <Text textAlign="center">If you still want to connect your bank or credit cards, close this window and start the setup again.</Text>}
        </Box>
      ),
    },
  }

  function renderErrorView() {
    return (
      <div className={'page-block'}>
        <Card>
          <PageState
            className={'img-block'}
            title={errorTypes[type].title}
            image={<img src={errorTypes[type].img}
            alt={errorTypes[type].alt} />}
            description={
              <React.Fragment>
                {errorTypes[type].description}
              </React.Fragment>
            }
          >
          </PageState>
        </Card>
        { redirectUrl && (
          <ButtonRow className="animate">
            <Button
              className="redirect-from-error-page"
              style={{marginBottom: '20px'}}
              onClick={handleCancel}
              tone="brand"
              variant="standard"
            >
              Go back to Manage Bankfeeds
            </Button>
          </ButtonRow> )}
      </div>
    );
  }

  return renderErrorView();
}

ErrorView.propTypes = {
  type: PropTypes.string,
  redirectUrl: PropTypes.string
}

export default ErrorView;
